import { FileRejection, useDropzone } from 'react-dropzone';
import { Box, Typography } from '@mui/material';

import { styled } from '@mui/material/styles';
import { UploadProps } from './type';
import Image from '../Image';
import RejectionFiles from './RejectionFiles';
import { IconImagePlus } from 'src/libraries/icons';

// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
}));

const DropZoneStyle = styled('div')({
  zIndex: 0,
  width: '100%',
  height: '100%',
  outline: 'none',
  display: 'flex',
  overflow: 'hidden',
  position: 'relative',
  alignItems: 'center',
  justifyContent: 'center',
  '& > *': { width: '100%', height: '100%' },
  '&:hover': {
    cursor: 'pointer',
    '& .placeholder': {
      zIndex: 9,
    },
  },
});

const PlaceholderStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  position: 'absolute',
  alignItems: 'center',
  flexDirection: 'column',
  justifyContent: 'center',
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.background.neutral,
  transition: theme.transitions.create('opacity', {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.shorter,
  }),
  '&:hover': { opacity: 0.72 },
}));

// ----------------------------------------------------------------------

export default function UploadAvatar({ error, errorMessage, file, helperText, sx, isIcon, ...other }: UploadProps) {
  const customFileRejection: FileRejection | null = errorMessage ? { file: new File([], 'customError'), errors: [{ message: errorMessage, code: 'file-invalid-type' }] } : null;
  const { getRootProps, getInputProps, isDragActive, isDragReject, fileRejections } = useDropzone({
    multiple: false,
    ...other,
  });

  return (
    <Box>
      <Box sx={{ display: 'flex' }}>
        <RootStyle
          sx={{
            ...((isDragReject || error) && {
              borderColor: 'error.light',
            }),
            width: isIcon ? '90px' : '160px',
            height: isIcon ? '90px' : '160px',
            ...sx,
          }}
        >
          <DropZoneStyle
            {...getRootProps()}
            sx={{
              ...(isDragActive && { opacity: 0.72 }),
            }}
          >
            <input {...getInputProps()} />

            {file && (
              <Image
                alt="avatar"
                src={typeof file === 'string' ? file : file.preview}
                sx={{ zIndex: 8, '& img': { objectFit: isIcon ? 'contain' : 'cover' } }}
              />
            )}

            <PlaceholderStyle
              className="placeholder"
              sx={{
                ...(file && {
                  opacity: 0,
                  color: 'common.white',
                  bgcolor: 'grey.900',
                  textAlign: 'center',
                  '&:hover': { opacity: 0.72 },
                }),
                ...((isDragReject || error) && {
                  bgcolor: 'error.lighter',
                }),
              }}
            >
              <IconImagePlus />
              <Typography mt={0.5} variant="caption">{file ? 'Update image' : 'Upload image'}</Typography>
            </PlaceholderStyle>
          </DropZoneStyle>
        </RootStyle>

        {helperText && helperText}
      </Box>

      {(fileRejections.length > 0 || customFileRejection) && <RejectionFiles fileRejections={customFileRejection ? [...fileRejections, customFileRejection] : fileRejections} />}
    </Box>
  );
}
